import { modalController } from '@ionic/vue';
import type { Route } from '@sentry/vue/build/types/router';
import { filter } from 'lodash';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import type { Router } from 'vue-router';
import { useRoute } from 'vue-router';

import {
  openPostCreateMobileModal,
  openTaskManagementAttachmentsModal,
  openTaskManagementMilestonesModal,
  openTaskManagementProjectSwitchModal,
} from './modalComponents';
import {
  openTaskManagementAttachmentsPopover,
  openAppDatePickerPopover,
  openTaskManagementMilestonesPopover,
  openTaskManagementNewColumnPopover,
  openTaskManagementProjectSwitchPopover,
  openTaskManagementViewModePopover,
  openUserListPopover,
  openTaskManagementMainHeaderSortingPopover,
  openTaskManagementAssignPopover,
  openTaskManagementStatusFilterPopover,
  openTaskManagementChecklistPopover,
} from './popoverComponents';

import {
  PostTypeActionEnum,
  TaskManagementMainHeaderMenuEnum,
  TaskManagementMainHeaderSearchModesEnum,
  TaskManagementMilestonesSortByEnum,
  TaskManagementMilestonesViewModeEnum,
  TaskManagementProjectsSortByEnum,
  TaskManagementTaskModalMenuCategoryEnum,
  TaskManagementTaskModalMenuItemActionEnum,
  TaskManagementTasksSortByEnum,
  TaskManagementViewModeEnum,
  TaskManagementActiveSortTypeEnum,
  TaskManagementSortDirectionEnum,
  TaskManagementAccessLevelEnum,
  UserRoleEnum,
  TaskManagementTaskHistoryActionType,
  DefaultUserItemTypesEnum,
  TaskManagementTasksStatusFilterEnum,
  AppIconsEnum,
} from '@/enums';
import { NotificationsPushActionsEnum } from '@/enums/notifications';
import { useTags, useToasts } from '@/helpers';
import { useI18n } from '@/i18n';
import { ROUTES_NAME } from '@/router';
import { useAppStore, useGroupsStore, useNetworkStore, useProjectsStore, useUserStore } from '@/store';
import type {
  TaskManagementTaskModel,
  TaskManagementMilestoneModel,
  TaskManagementModalMenuItemModel,
  TaskManagementTaskModalMenuModel,
  TaskManagementProjectModel,
  TaskManagementMenuItemModel,
} from '@/types';

type IUseTaskManagement = {
  getTaskMainMenu(): TaskManagementTaskModalMenuModel[];
  getTaskMenuFeatures(): TaskManagementTaskModalMenuModel;
  getTaskMenuActions(
    withActions: boolean,
    taskIsClosed: boolean,
    taskIsArchived: boolean,
    taskNotify: boolean | null,
    userIsAssigned: boolean,
    projectId: number
  ): TaskManagementTaskModalMenuModel;
  getTaskModalMenuItems(
    withActions: boolean,
    taskIsClosed: boolean,
    taskIsArchived: boolean,
    taskNotify: boolean | null,
    userIsAssigned: boolean,
    projectId: number
  ): TaskManagementTaskModalMenuModel[];
  taskManagementMilestonesHelper(
    ev: Event,
    cardData: TaskManagementTaskModel | null,
    milestoneData: TaskManagementMilestoneModel | null,
    projectId: number,
    onlyCreate: boolean
  ): Promise<TaskManagementMilestoneModel | undefined>;
  menuSelectionHandling(
    ev: Event,
    value: TaskManagementMenuItemModel,
    taskData: TaskManagementTaskModel,
    projectId: number
  ): Promise<boolean>;
  taskManagementProjectSwitchHelper(
    ev: Event,
    isPostCreation: boolean,
    withRouting: boolean,
    isOnlyProjectCreation: boolean,
    selectedGroupId?: number | undefined
  ): Promise<void>;
  getMainHeaderMenu(
    showProjectControls: boolean,
    showBoardControls: boolean,
    showMilestonesControls: boolean
  ): TaskManagementModalMenuItemModel[];
  mainHeaderMenuSelectionHandling(
    ev: Event,
    value: TaskManagementMainHeaderMenuEnum,
    router: Router,
    projectId: number
  ): Promise<boolean>;
  getMainHeaderSearchModes(): TaskManagementModalMenuItemModel[];
  getTasksSortingModes(): TaskManagementModalMenuItemModel[];
  getMilestonesSortingModes(): TaskManagementModalMenuItemModel[];
  getProjectsSortingModes(): TaskManagementModalMenuItemModel[];
  taskManagementMainHeaderSearchHelper(searchQuery: string, projectId: number, route: Route): Promise<void>;
  // Разрешено ли пользователю управление проектом
  // Можно только если он админ группы, в которой находится проект
  accessToCreateProject(groupId: number): boolean;
  // Разрешено ли пользователю управление майлстоунами проекта
  // Можно только если он админ группы, в которой находится проект
  accessToCreateMilestone(projectId: number): boolean;
  // Разрешено ли пользователю удалить задачу
  // Можно только если это создатель проекта
  accessToDeleteTask(projectId: number): boolean;
  getSortTitle(
    sort: TaskManagementTasksSortByEnum | TaskManagementMilestonesSortByEnum | TaskManagementProjectsSortByEnum
  ): string;
  getSelectedSort():
    | TaskManagementTasksSortByEnum
    | TaskManagementMilestonesSortByEnum
    | TaskManagementProjectsSortByEnum;
  getSelectedSortDirection(): TaskManagementSortDirectionEnum;
  /* Включен ли taskManagement */
  getAccessToTaskManagement(): boolean;
  loadProject(projectId: number): Promise<void>;
  loadTasks(projectId: number): Promise<void>;
  refresh(projectId: number): Promise<void>;
  setSortDirection(): void;
  getHistoryTitle(action: TaskManagementTaskHistoryActionType | NotificationsPushActionsEnum): string;
  // Разрешено ли создавать задачи в сети в целом
  getAccessToCreateTask(): boolean;
  getSelectedStatusFilter(): TaskManagementTasksStatusFilterEnum | TaskManagementMilestonesViewModeEnum;
  getSelectedStatusFilterTitle(
    status: TaskManagementTasksStatusFilterEnum | TaskManagementMilestonesViewModeEnum
  ): string;
  getTasksStatusFilters(): TaskManagementModalMenuItemModel[];
  getViewModeIcon(viewMode: TaskManagementViewModeEnum): AppIconsEnum;
};

export const useTaskManagement = (): IUseTaskManagement => {
  const getTaskMainMenu = (): TaskManagementTaskModalMenuModel[] => {
    const { t } = useI18n();

    const data = [
      {
        title: t('taskManagement.tasks.addToTask'),
        value: TaskManagementTaskModalMenuCategoryEnum.Features,
        icon: AppIconsEnum.None,
        data: [],
      },
      {
        title: t('actions'),
        value: TaskManagementTaskModalMenuCategoryEnum.Actions,
        icon: AppIconsEnum.None,
        data: [],
      },
    ];

    return data;
  };

  const getTaskMenuFeatures = (): TaskManagementTaskModalMenuModel => {
    const { t } = useI18n();

    const data: TaskManagementModalMenuItemModel[] = [
      {
        title: t('appPopoverMenu.taskManagementMenu.assign'),
        icon: AppIconsEnum.Assignee,
        disabled: false,
        value: TaskManagementTaskModalMenuItemActionEnum.Assignee,
      },
      {
        title: t('taskManagement.participants'),
        icon: AppIconsEnum.Users,
        disabled: false,
        value: TaskManagementTaskModalMenuItemActionEnum.Participants,
      },
      {
        title: t('taskManagement.tags.title'),
        icon: AppIconsEnum.Label,
        disabled: false,
        value: TaskManagementTaskModalMenuItemActionEnum.Tag,
      },
      {
        title: t('taskManagement.checklist.title'),
        icon: AppIconsEnum.TodoDone,
        disabled: false,
        value: TaskManagementTaskModalMenuItemActionEnum.Checklist,
      },
      {
        title: t('appPopoverMenu.taskManagementMenu.dueDate'),
        icon: AppIconsEnum.Timer,
        disabled: false,
        value: TaskManagementTaskModalMenuItemActionEnum.Date,
      },
      {
        title: t('appPopoverMenu.taskManagementMenu.attachments'),
        icon: AppIconsEnum.Paperclip,
        disabled: false,
        value: TaskManagementTaskModalMenuItemActionEnum.Attachments,
      },
      {
        title: t('taskManagement.milestones.title'),
        icon: AppIconsEnum.Calendar,
        disabled: false,
        value: TaskManagementTaskModalMenuItemActionEnum.Milestone,
      },
    ];

    const features = {
      value: TaskManagementTaskModalMenuCategoryEnum.Features,
      title: t('taskManagement.tasks.addToTask'),
      icon: AppIconsEnum.None,
      data: filter(data, ({ disabled }) => !disabled),
    };

    return features;
  };

  const getTaskMenuActions = (
    withActions: boolean,
    taskIsClosed: boolean,
    taskIsArchived: boolean,
    taskNotify: boolean | null,
    userIsAssigned: boolean,
    projectId: number
  ) => {
    const { t } = useI18n();
    const data: TaskManagementModalMenuItemModel[] = [
      /** @note Currently not in use */
      // {
      //   value: TaskManagementTaskModalMenuItemActionEnum.Move,
      //   title: t('appPopoverMenu.move.title'),
      //   icon: AppIconsEnum.Move,
      //   disabled: false,
      // },
      // {
      //   value: TaskManagementTaskModalMenuItemActionEnum.Copy,
      //   title: t('appPopoverMenu.copy.title'),
      //   icon: AppIconsEnum.CopyToClipboard,
      //   disabled: false,
      // },

      /** @note Currently not in use. Now located in the TaskManagementAssignPopover component */
      // {
      //   value: TaskManagementTaskModalMenuItemActionEnum.RemoveAssignee,
      //   title: t('taskManagement.tasks.removeAssignee'),
      //   icon: AppIconsEnum.Assignee,
      //   enabled: userIsAssigned,
      // },

      {
        title: taskIsClosed
          ? t('appPopoverMenu.taskManagementMenu.open')
          : t('appPopoverMenu.taskManagementMenu.close'),
        icon: taskIsClosed ? AppIconsEnum.LockOpen : AppIconsEnum.LockClosed,
        disabled: false,
        value: TaskManagementTaskModalMenuItemActionEnum.Close,
      },

      /** @note Currently not in use. #1352 Temporary disabled */
      // {
      //   value: TaskManagementTaskModalMenuItemActionEnum.Archive,
      //   title: taskIsArchived ? t('taskManagement.tasks.dearchive') : t('taskManagement.tasks.archive'),
      //   icon: AppIconsEnum.Archive,
      //   disabled: false,
      // },

      {
        title: taskNotify
          ? t('taskManagement.tasks.notificationsEnabled')
          : t('taskManagement.tasks.notificationsDisabled'),
        icon: taskNotify ? AppIconsEnum.Notifications : AppIconsEnum.NotificationsOff,
        // enabled: taskNotify !== null,
        disabled: taskNotify === null,
        value: TaskManagementTaskModalMenuItemActionEnum.Notifications,
      },

      /** @note Currently not in use */
      // {
      //   value: TaskManagementTaskModalMenuItemActionEnum.Share,
      //   title: t('appPopoverMenu.share.title'),
      //   icon: AppIconsEnum.Share,
      //   disabled: false,
      // },

      {
        title: t('appPopoverMenu.delete.title'),
        icon: AppIconsEnum.Trash,
        // enabled: accessToDeleteTask(projectId),
        disabled: !accessToDeleteTask(projectId),
        value: TaskManagementTaskModalMenuItemActionEnum.Delete,
      },
    ];

    const actions = {
      value: TaskManagementTaskModalMenuCategoryEnum.Actions,
      title: t('actions'),
      icon: AppIconsEnum.None,
      data: filter(data, ({ disabled }) => !disabled),
    };

    return actions;
  };

  const getTaskModalMenuItems = (
    withActions: boolean,
    taskIsClosed: boolean,
    taskIsArchived: boolean,
    taskNotify: boolean | null,
    userIsAssigned: boolean,
    projectId: number
  ): TaskManagementTaskModalMenuModel[] => {
    const features = getTaskMenuFeatures();

    if (withActions) {
      const actions = getTaskMenuActions(
        withActions,
        taskIsClosed,
        taskIsArchived,
        taskNotify,
        userIsAssigned,
        projectId
      );
      return [features, actions];
    } else {
      return [features];
    }
  };

  const menuSelectionHandling = async (
    ev: Event,
    value: TaskManagementMenuItemModel,
    taskData: TaskManagementTaskModel,
    projectId: number
  ): Promise<boolean> => {
    const { t } = useI18n();
    const { showSonnerToast } = useToasts();

    const projectsStore = useProjectsStore();
    const taskManagementHelper = useTaskManagement();
    const tagsHelper = useTags();
    switch (value) {
      case TaskManagementTaskModalMenuItemActionEnum.Assignee:
        {
          const project = projectsStore.getProjectById(projectId);
          if (project?.group.id) {
            const result = await openTaskManagementAssignPopover(ev, project.group.id, taskData);
            if (result === null) {
              await projectsStore.taskRemoveAssignee(taskData.id);
            } else if (result) {
              await projectsStore.taskSetAssignee(taskData.id, result);
            }
          }
        }
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Participants:
        {
          const project = projectsStore.getProjectById(projectId);
          await openUserListPopover(ev, [], true, project?.group.id, taskData);
        }
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Tag:
        {
          const result = await tagsHelper.tagsPicker(ev, taskData.tags.data, true, true);
          if (result) {
            const addedTags = result.filter(
              (tag) => !taskData.tags.data.some((existingTag) => existingTag.id === tag.id)
            );
            const removedTags = taskData.tags.data.filter(
              (existingTag) => !result.some((tag) => tag.id === existingTag.id)
            );

            console.log('Added Tags:', addedTags);
            console.log('Removed Tags:', removedTags);

            if (addedTags.length > 0) {
              await projectsStore.addTagToTask(taskData.id, addedTags);
            }

            if (removedTags.length > 0) {
              await projectsStore.removeTagFromTask(taskData.id, removedTags);
            }
          }
        }
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Checklist:
        {
          await openTaskManagementChecklistPopover(ev, taskData);
        }
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Date:
        {
          const result = await openAppDatePickerPopover(ev, taskData.dateDue, true);
          if (result) {
            result
              ? await projectsStore.updateTaskDueDate(taskData.id, result)
              : await projectsStore.updateTaskDueDate(taskData.id, null);
          }
        }
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Attachments:
        await taskManagementAttachmentsHelper(ev, taskData);
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Milestone:
        await taskManagementMilestonesHelper(ev, taskData, null, projectId, false);
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Move:
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Copy:
        break;

      /* case TaskManagementTaskModalMenuItemActionEnum.RemoveAssignee:
        await projectsStore.taskRemoveAssignee(taskData.id);
        break; */

      case TaskManagementTaskModalMenuItemActionEnum.Close:
        await projectsStore.updateTaskStatus(taskData.id);
        await modalController.getTop().then((modal) => {
          return modal ? modalController.dismiss() : null;
        });
        await taskManagementHelper.refresh(Number(projectId));
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Archive:
        {
          if (taskData.isArchived) {
            const result = await projectsStore.taskDearchive(taskData.id);
            if (result) {
              showSonnerToast(t('taskManagement.history.actions.dearchived'), true);
            }
          } else {
            const result = await projectsStore.taskArchive(taskData.id);
            if (result) {
              showSonnerToast(t('taskManagement.history.actions.archived'), true);
            }
          }
        }
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Notifications:
        {
          if (taskData?.notify !== null) {
            taskData.notify
              ? await projectsStore.taskDisableNotifications(taskData.id)
              : await projectsStore.taskEnableNotifications(taskData.id);
          }
        }
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Share:
        break;

      case TaskManagementTaskModalMenuItemActionEnum.Delete:
        {
          /* await router.replace({ query: undefined }); */
          await modalController.getTop().then((modal) => {
            return modal ? modalController.dismiss() : null;
          });
          await projectsStore.deleteTask(taskData.id);
        }

        break;

      default:
        break;
    }
    return true;
  };

  const taskManagementMilestonesHelper = async (
    ev: Event,
    cardData: TaskManagementTaskModel | null,
    milestoneData: TaskManagementMilestoneModel | null,
    projectId: number,
    onlyCreate: boolean
  ): Promise<TaskManagementMilestoneModel | undefined> => {
    const appStore = useAppStore();
    if (appStore.isMDWidth) {
      return await openTaskManagementMilestonesPopover(ev, cardData, milestoneData, projectId, onlyCreate);
    }
    return await openTaskManagementMilestonesModal(cardData, milestoneData, projectId, onlyCreate);
  };

  const taskManagementAttachmentsHelper = async (ev: Event, taskData: TaskManagementTaskModel): Promise<void> => {
    const appStore = useAppStore();
    if (appStore.isMDWidth) {
      return await openTaskManagementAttachmentsPopover(ev, taskData.id);
    } else {
      return await openTaskManagementAttachmentsModal(taskData.id);
    }
  };

  const taskManagementProjectSwitchHelper = async (
    ev: Event,
    isPostCreation: boolean,
    withRouting: boolean,
    isOnlyProjectCreation: boolean,
    selectedGroupId?: number | undefined
  ): Promise<void> => {
    const appStore = useAppStore();
    if (appStore.isMDWidth) {
      await openTaskManagementProjectSwitchPopover(
        ev,
        isPostCreation,
        isOnlyProjectCreation,
        withRouting,
        selectedGroupId
      );
    } else {
      await openTaskManagementProjectSwitchModal(isPostCreation, isOnlyProjectCreation, withRouting, selectedGroupId);
    }
  };

  const getViewModeIcon = (viewMode: TaskManagementViewModeEnum): AppIconsEnum => {
    const viewModeIcons: Record<TaskManagementViewModeEnum, AppIconsEnum> = {
      [TaskManagementViewModeEnum.Board]: AppIconsEnum.ContainerImage,
      [TaskManagementViewModeEnum.List]: AppIconsEnum.List,
      [TaskManagementViewModeEnum.Table]: AppIconsEnum.Table,
    };
    return viewModeIcons[viewMode];
  };

  const getMainHeaderMenu = (
    showProjectControls: boolean,
    showBoardControls: boolean,
    showMilestonesControls: boolean
  ): TaskManagementModalMenuItemModel[] => {
    const { t } = useI18n();
    const route = useRoute();
    const projectsStore = useProjectsStore();
    const appStore = useAppStore();
    const viewMode: ComputedRef<TaskManagementViewModeEnum> = computed(() => projectsStore.viewMode);
    const milestonesViewMode: ComputedRef<TaskManagementMilestonesViewModeEnum> = computed(
      () => projectsStore.statusFilter.milestones
    );
    const isMDWidth: ComputedRef<boolean> = computed(() => appStore.isMDWidth);
    const items: TaskManagementModalMenuItemModel[] = [];

    if (showProjectControls) {
      items.push({
        title: t('taskManagement.projects.selectProject'),
        icon: AppIconsEnum.Substitute,
        disabled: false,
        value: TaskManagementMainHeaderMenuEnum.SwitchProjects,
      });
    }

    if (showBoardControls) {
      items.push(
        {
          title: t('taskManagement.projects.switchProjectViewMode'),
          icon: getViewModeIcon(viewMode.value),
          // enabled: !isMDWidth.value,
          disabled: isMDWidth.value,
          value: TaskManagementMainHeaderMenuEnum.SwitchViewMode,
        },
        {
          title: t('taskManagement.projects.expand'),
          icon: AppIconsEnum.Expand,
          // enabled: route.name !== ROUTES_NAME.PROJECT_BY_ID,
          disabled: route.name === ROUTES_NAME.PROJECT_BY_ID,
          value: TaskManagementMainHeaderMenuEnum.Expand,
        },
        {
          title: t('taskManagement.columns.create'),
          icon: AppIconsEnum.PencilSquare,
          // enabled: viewMode.value === TaskManagementViewModeEnum.Board,
          disabled: viewMode.value !== TaskManagementViewModeEnum.Board,
          value: TaskManagementMainHeaderMenuEnum.CreateColumn,
        },
        {
          title: t('taskManagement.tasks.create'),
          icon: AppIconsEnum.PencilSquare,
          // enabled: getAccessToCreateTask(),
          disabled: !getAccessToCreateTask(),
          value: TaskManagementMainHeaderMenuEnum.CreateTask,
        },
        {
          title: t('taskManagement.status.title'),
          icon: AppIconsEnum.Eye,
          // enabled: viewMode.value !== TaskManagementViewModeEnum.Board,
          disabled: viewMode.value === TaskManagementViewModeEnum.Board,
          value: TaskManagementMainHeaderMenuEnum.ChangeStatusFilter,
          subtitle: getSelectedStatusFilterTitle(getSelectedStatusFilter()),
        },
        {
          title: getSortTitle(getSelectedSort()),

          icon: AppIconsEnum.Settings,
          // enabled: viewMode.value !== TaskManagementViewModeEnum.Board && !isMDWidth.value,
          disabled: viewMode.value === TaskManagementViewModeEnum.Board || isMDWidth.value,
          value: TaskManagementMainHeaderMenuEnum.OpenSortingMenu,
        },
        {
          title:
            getSelectedSortDirection() === TaskManagementSortDirectionEnum.Asc
              ? t('sorting.direction.asc')
              : t('sorting.direction.desc'),
          icon:
            getSelectedSortDirection() === TaskManagementSortDirectionEnum.Asc
              ? AppIconsEnum.ArrowUp
              : AppIconsEnum.ArrowDown,
          // enabled: viewMode.value !== TaskManagementViewModeEnum.Board && !isMDWidth.value,
          disabled: viewMode.value === TaskManagementViewModeEnum.Board || isMDWidth.value,
          value: TaskManagementMainHeaderMenuEnum.SetSort,
        },
        {
          title: t('taskManagement.tasks.refresh'),
          icon: AppIconsEnum.Refresh,
          disabled: false,
          value: TaskManagementMainHeaderMenuEnum.Refresh,
        }
        /** @note Currently not in use */
        // {
        //   value: TaskManagementMainHeaderMenuEnum.EditBoard,
        //   title: t('taskManagement.board.edit'),
        //   icon: AppIconsEnum.PencilSquare,
        //   enabled: viewMode.value === TaskManagementViewModeEnum.Board,
        // }
      );
    }

    if (showMilestonesControls) {
      items.push(
        {
          title:
            milestonesViewMode.value === TaskManagementMilestonesViewModeEnum.Closed
              ? t('taskManagement.status.showOnlyOpened')
              : t('taskManagement.status.showOnlyClosed'),
          icon:
            milestonesViewMode.value === TaskManagementMilestonesViewModeEnum.Closed
              ? AppIconsEnum.LockOpen
              : AppIconsEnum.LockClosed,
          disabled: false,
          value: TaskManagementMainHeaderMenuEnum.SwitchMilestonesViewMode,
        },
        {
          title: t('taskManagement.milestones.create'),
          icon: AppIconsEnum.PencilSquare,
          disabled: false,
          value: TaskManagementMainHeaderMenuEnum.CreateMilestone,
        }
      );
    }

    return filter(items, ({ disabled }) => !disabled);
  };

  const mainHeaderMenuSelectionHandling = async (
    ev: Event,
    value: TaskManagementMainHeaderMenuEnum,
    router: Router,
    projectId: number
  ): Promise<boolean> => {
    const projectsStore = useProjectsStore();
    const milestonesViewMode: ComputedRef<TaskManagementMilestonesViewModeEnum> = computed(
      () => projectsStore.statusFilter.milestones
    );
    const currentProject: ComputedRef<TaskManagementProjectModel> = computed(() => projectsStore.getCurrentProject);
    switch (value) {
      case TaskManagementMainHeaderMenuEnum.SwitchProjects:
        taskManagementProjectSwitchHelper(ev, false, true, false, undefined);
        break;

      case TaskManagementMainHeaderMenuEnum.SwitchViewMode:
        await openTaskManagementViewModePopover(ev);
        break;

      case TaskManagementMainHeaderMenuEnum.Expand:
        {
          await router.push({
            name: ROUTES_NAME.PROJECT_BY_ID,
            params: { projectId: currentProject.value.id },
          });
        }
        break;

      case TaskManagementMainHeaderMenuEnum.CreateColumn:
        await openTaskManagementNewColumnPopover(ev, currentProject.value);
        break;

      case TaskManagementMainHeaderMenuEnum.CreateTask:
        await openPostCreateMobileModal(PostTypeActionEnum.Task);
        break;

      case TaskManagementMainHeaderMenuEnum.Refresh:
        await refresh(Number(projectId));
        break;

      case TaskManagementMainHeaderMenuEnum.OpenSortingMenu:
        await openTaskManagementMainHeaderSortingPopover(ev);
        break;

      case TaskManagementMainHeaderMenuEnum.EditBoard:
        break;

      case TaskManagementMainHeaderMenuEnum.SwitchMilestonesViewMode:
        await changeMilestonesViewMode(
          ev,
          milestonesViewMode.value === TaskManagementMilestonesViewModeEnum.Opened
            ? TaskManagementMilestonesViewModeEnum.Closed
            : TaskManagementMilestonesViewModeEnum.Opened
        );
        break;

      case TaskManagementMainHeaderMenuEnum.CreateMilestone:
        await taskManagementMilestonesHelper(ev, null, null, projectId, true);
        break;

      case TaskManagementMainHeaderMenuEnum.SetSort:
        setSortDirection();
        break;

      case TaskManagementMainHeaderMenuEnum.ChangeStatusFilter:
        await openTaskManagementStatusFilterPopover(ev);
        break;

      default:
        break;
    }
    return true;
  };

  const changeMilestonesViewMode = async (ev: Event, mode: TaskManagementMilestonesViewModeEnum) => {
    const projectsStore = useProjectsStore();
    projectsStore.$patch((state) => {
      state.statusFilter.milestones = mode;
    });
  };

  const getMainHeaderSearchModes = (): TaskManagementModalMenuItemModel[] => {
    const { t } = useI18n();
    const data = [
      /** @note Currently not in use */
      // {
      //   value: TaskManagementMainHeaderSearchModesEnum.Name,
      //   title: t('table.title'),
      //   icon: AppIconsEnum.Letters,
      //   disabled: false,
      // },
      {
        title: t('appPopoverMenu.taskManagementMenu.assignee'),
        icon: AppIconsEnum.Assignee,
        disabled: false,
        value: TaskManagementMainHeaderSearchModesEnum.Assigne,
      },
      {
        title: t('taskManagement.author'),
        icon: AppIconsEnum.Assignee,
        disabled: false,
        value: TaskManagementMainHeaderSearchModesEnum.Author,
      },
      {
        title: t('taskManagement.tags.title'),
        icon: AppIconsEnum.Label,
        disabled: false,
        value: TaskManagementMainHeaderSearchModesEnum.Tag,
      },
      {
        title: t('taskManagement.milestones.title'),
        icon: AppIconsEnum.Calendar,
        disabled: false,
        value: TaskManagementMainHeaderSearchModesEnum.Milestone,
      },
    ];
    return data;
  };

  const getTasksSortingModes = (): TaskManagementModalMenuItemModel[] => {
    const data = [
      {
        value: TaskManagementTasksSortByEnum.Identifier,
        title: getSortTitle(TaskManagementTasksSortByEnum.Identifier),
        icon: AppIconsEnum.None,
        disabled: false,
      },
      {
        value: TaskManagementTasksSortByEnum.Title,
        title: getSortTitle(TaskManagementTasksSortByEnum.Title),
        icon: AppIconsEnum.None,
        disabled: false,
      },
      {
        value: TaskManagementTasksSortByEnum.DateCreated,
        title: getSortTitle(TaskManagementTasksSortByEnum.DateCreated),
        icon: AppIconsEnum.None,
        disabled: false,
      },
      /** @note Currently not in use */
      // {
      //   value: TaskManagementTasksSortByEnum.DateStart,
      //   title: getSortTitle(TaskManagementTasksSortByEnum.DateStart),
      //   icon: AppIconsEnum.None,
      //   disabled: false,
      // },
      {
        value: TaskManagementTasksSortByEnum.DateDue,
        title: getSortTitle(TaskManagementTasksSortByEnum.DateDue),
        icon: AppIconsEnum.None,
        disabled: false,
      },
      {
        value: TaskManagementTasksSortByEnum.DateLastUpdated,
        title: getSortTitle(TaskManagementTasksSortByEnum.DateLastUpdated),
        icon: AppIconsEnum.None,
        disabled: false,
      },
      {
        value: TaskManagementTasksSortByEnum.MilestoneDateDue,
        title: getSortTitle(TaskManagementTasksSortByEnum.MilestoneDateDue),
        icon: AppIconsEnum.None,
        disabled: false,
      },
    ];

    return data;
  };

  const getMilestonesSortingModes = (): TaskManagementModalMenuItemModel[] => {
    const data: TaskManagementModalMenuItemModel[] = [
      {
        title: getSortTitle(TaskManagementMilestonesSortByEnum.Title),
        icon: AppIconsEnum.None,
        disabled: false,
        value: TaskManagementMilestonesSortByEnum.Title,
      },
      {
        title: getSortTitle(TaskManagementMilestonesSortByEnum.DateCreated),
        icon: AppIconsEnum.None,
        disabled: false,
        value: TaskManagementMilestonesSortByEnum.DateCreated,
      },
      {
        title: getSortTitle(TaskManagementMilestonesSortByEnum.DateStart),
        icon: AppIconsEnum.None,
        disabled: false,
        value: TaskManagementMilestonesSortByEnum.DateStart,
      },
      {
        title: getSortTitle(TaskManagementMilestonesSortByEnum.DateDue),
        icon: AppIconsEnum.None,
        disabled: false,
        value: TaskManagementMilestonesSortByEnum.DateDue,
      },
    ];

    return data;
  };

  const getProjectsSortingModes = (): TaskManagementModalMenuItemModel[] => {
    const data: TaskManagementModalMenuItemModel[] = [
      {
        value: TaskManagementProjectsSortByEnum.Title,
        title: getSortTitle(TaskManagementProjectsSortByEnum.Title),
        icon: AppIconsEnum.None,
        disabled: false,
      },
      {
        value: TaskManagementProjectsSortByEnum.DateCreated,
        title: getSortTitle(TaskManagementProjectsSortByEnum.DateCreated),
        icon: AppIconsEnum.None,
        disabled: false,
      },
      {
        value: TaskManagementProjectsSortByEnum.DateDue,
        title: getSortTitle(TaskManagementProjectsSortByEnum.DateDue),
        icon: AppIconsEnum.None,
        disabled: false,
      },
      {
        value: TaskManagementProjectsSortByEnum.GroupTitle,
        title: getSortTitle(TaskManagementProjectsSortByEnum.GroupTitle),
        icon: AppIconsEnum.None,
        disabled: false,
      },
    ];

    return data;
  };

  const getSortTitle = (
    sort: TaskManagementTasksSortByEnum | TaskManagementMilestonesSortByEnum | TaskManagementProjectsSortByEnum
  ) => {
    const { t } = useI18n();
    switch (sort) {
      case TaskManagementTasksSortByEnum.Identifier:
        return t('sorting.byIdentifier');

      case TaskManagementTasksSortByEnum.Title:
      case TaskManagementProjectsSortByEnum.Title:
      case TaskManagementMilestonesSortByEnum.Title:
        return t('sorting.byTitle');

      case TaskManagementTasksSortByEnum.DateCreated:
      case TaskManagementProjectsSortByEnum.DateCreated:
      case TaskManagementMilestonesSortByEnum.DateCreated:
        return t('sorting.byDateCreated');

      case TaskManagementTasksSortByEnum.DateStart:
      case TaskManagementMilestonesSortByEnum.DateStart:
        return t('sorting.byDateStart');

      case TaskManagementTasksSortByEnum.DateDue:
      case TaskManagementProjectsSortByEnum.DateDue:
      case TaskManagementMilestonesSortByEnum.DateDue:
        return t('sorting.byDateDue');

      case TaskManagementTasksSortByEnum.DateLastUpdated:
        return t('sorting.byDateUpdate');

      case TaskManagementTasksSortByEnum.MilestoneDateDue:
        return t('sorting.byMilestoneDateDue');

      case TaskManagementProjectsSortByEnum.GroupTitle:
        return t('sorting.byGroupTitle');

      default:
        return t('sorting.byIdentifier');
    }
  };

  const taskManagementMainHeaderSearchHelper = async (
    searchQuery: string,
    projectId: number,
    route: Route
  ): Promise<void> => {
    const projectsStore = useProjectsStore();
    const viewMode = projectsStore.viewMode;
    if (
      route.name === ROUTES_NAME.PROJECT_BY_ID ||
      route.name === ROUTES_NAME.GROUP_BY_ID ||
      route.name === ROUTES_NAME.TASKS
    ) {
      if (viewMode === TaskManagementViewModeEnum.Board) {
        projectsStore.tasksSearchByAllColumns(searchQuery);
      } else {
        projectsStore.searchByTask(searchQuery, projectId);
      }
    }

    if (route.name === ROUTES_NAME.MILESTONES) {
      projectsStore.searchByMilestone(searchQuery, projectId);
    }
  };

  const accessToCreateProject = (groupId: number): boolean => {
    const userStore = useUserStore();
    const groupStore = useGroupsStore();
    const group = groupStore.getGroupById(groupId);
    const currentUserId = userStore.current?.id ?? 0;

    return group ? group.adminIds.includes(currentUserId) : false;
  };

  const accessToCreateMilestone = (projectId: number): boolean => {
    const userStore = useUserStore();
    const groupStore = useGroupsStore();
    const projectsStore = useProjectsStore();

    const project = projectsStore.getProjectById(projectId);
    if (project) {
      const currentUserId = userStore.current?.id ?? 0;
      const group = groupStore.getGroupById(project.group.id);
      return group ? group.adminIds.includes(currentUserId) : false;
    }

    return false;
  };

  const accessToDeleteTask = (projectId: number): boolean => {
    const projectsStore = useProjectsStore();
    const userStore = useUserStore();
    const project = projectsStore.getProjectById(projectId);
    const currentUserId = userStore.current?.id ?? 0;
    return project?.author?.id === currentUserId || false;
  };

  const getAccessToCreateTask = (): boolean => {
    const networkStore = useNetworkStore();
    return (
      getAccessToTaskManagement() &&
      (networkStore.settings?.defaultUserItemTypes?.includes(DefaultUserItemTypesEnum.Task) ?? false)
    );
  };

  const getSelectedSort = ():
    | TaskManagementTasksSortByEnum
    | TaskManagementMilestonesSortByEnum
    | TaskManagementProjectsSortByEnum => {
    const projectsStore = useProjectsStore();
    const activeSortType = projectsStore.sorting.activeSortType;

    switch (activeSortType) {
      case TaskManagementActiveSortTypeEnum.Tasks:
        return projectsStore.sorting.tasks.sortBy;

      case TaskManagementActiveSortTypeEnum.Milestones:
        return projectsStore.sorting.milestones.sortBy;

      case TaskManagementActiveSortTypeEnum.Projects:
        return projectsStore.sorting.projects.sortBy;

      default:
        return projectsStore.sorting.tasks.sortBy;
    }
  };

  const getSelectedSortDirection = (): TaskManagementSortDirectionEnum => {
    const projectsStore = useProjectsStore();
    const activeSortType = projectsStore.sorting.activeSortType;

    switch (activeSortType) {
      case TaskManagementActiveSortTypeEnum.Tasks:
        return projectsStore.sorting.tasks.direction;

      case TaskManagementActiveSortTypeEnum.Milestones:
        return projectsStore.sorting.milestones.direction;

      case TaskManagementActiveSortTypeEnum.Projects:
        return projectsStore.sorting.projects.direction;

      default:
        return projectsStore.sorting.tasks.direction;
    }
  };

  const setSortDirection = (): void => {
    const projectsStore = useProjectsStore();

    const activeSortType = projectsStore.sorting.activeSortType;
    const selectedSortDirection = getSelectedSortDirection();

    // Tasks
    if (activeSortType === TaskManagementActiveSortTypeEnum.Tasks) {
      projectsStore.$patch((state) => {
        state.sorting.tasks.direction =
          selectedSortDirection === TaskManagementSortDirectionEnum.Asc
            ? TaskManagementSortDirectionEnum.Desc
            : TaskManagementSortDirectionEnum.Asc;
      });
    }

    // Milestones
    if (activeSortType === TaskManagementActiveSortTypeEnum.Milestones) {
      projectsStore.$patch((state) => {
        state.sorting.milestones.direction =
          selectedSortDirection === TaskManagementSortDirectionEnum.Asc
            ? TaskManagementSortDirectionEnum.Desc
            : TaskManagementSortDirectionEnum.Asc;
      });
    }

    // Projects
    if (activeSortType === TaskManagementActiveSortTypeEnum.Projects) {
      projectsStore.$patch((state) => {
        state.sorting.projects.direction =
          selectedSortDirection === TaskManagementSortDirectionEnum.Asc
            ? TaskManagementSortDirectionEnum.Desc
            : TaskManagementSortDirectionEnum.Asc;
      });
    }
  };

  const getAccessToTaskManagement = (): boolean => {
    const networkStore = useNetworkStore();
    const userStore = useUserStore();
    const currentUserRoleId: UserRoleEnum = userStore.current?.roleId ?? 0;

    const taskManagementAccessLevel =
      networkStore.settings?.taskManagementAccessLevel ?? TaskManagementAccessLevelEnum.None;

    switch (taskManagementAccessLevel) {
      case TaskManagementAccessLevelEnum.None:
        return false;

      case TaskManagementAccessLevelEnum.User:
        return true;

      case TaskManagementAccessLevelEnum.Administrator:
        return currentUserRoleId >= UserRoleEnum.Administrator ? true : false;

      default:
        return false;
    }
  };

  const loadProject = async (projectId: number): Promise<void> => {
    const projectsStore = useProjectsStore();
    const currentProject = projectsStore.getCurrentProject;

    await projectsStore.projectById(projectId);
    if (currentProject.id !== projectId) await projectsStore.switchProject(projectId);
  };

  const loadTasks = async (projectId: number): Promise<void> => {
    const projectsStore = useProjectsStore();

    const viewMode = projectsStore.viewMode;
    const searchQuery = projectsStore.getTasksSearchQuery;

    viewMode !== TaskManagementViewModeEnum.Board && searchQuery.length === 0
      ? await projectsStore.tasksByProjectId(projectId)
      : false;
  };

  const refresh = async (projectId: number): Promise<void> => {
    const projectsStore = useProjectsStore();
    const viewMode = projectsStore.viewMode;

    await loadProject(projectId);
    await loadTasks(projectId);

    if (viewMode === TaskManagementViewModeEnum.Board) {
      await projectsStore.tasksByAllColumns();
    }
  };

  const getHistoryTitle = (action: TaskManagementTaskHistoryActionType | NotificationsPushActionsEnum): string => {
    const { t } = useI18n();
    const actionMapping: Partial<Record<TaskManagementTaskHistoryActionType | NotificationsPushActionsEnum, string>> = {
      [TaskManagementTaskHistoryActionType.Created]: 'taskManagement.history.actions.created',
      [NotificationsPushActionsEnum.TaskCreated]: 'taskManagement.history.actions.created',

      [TaskManagementTaskHistoryActionType.Closed]: 'taskManagement.history.actions.closed',
      [NotificationsPushActionsEnum.TaskClosed]: 'taskManagement.history.actions.closed',

      [TaskManagementTaskHistoryActionType.Reopened]: 'taskManagement.history.actions.reopened',
      [NotificationsPushActionsEnum.TaskReopened]: 'taskManagement.history.actions.reopened',

      [TaskManagementTaskHistoryActionType.Archived]: 'taskManagement.history.actions.archived',
      [NotificationsPushActionsEnum.TaskArchived]: 'taskManagement.history.actions.archived',

      [TaskManagementTaskHistoryActionType.Dearchived]: 'taskManagement.history.actions.dearchived',
      [NotificationsPushActionsEnum.TaskDeArchived]: 'taskManagement.history.actions.dearchived',

      [TaskManagementTaskHistoryActionType.TitleUpdated]: 'taskManagement.history.actions.titleUpdated',
      [NotificationsPushActionsEnum.TaskTitleUpdated]: 'taskManagement.history.actions.titleUpdated',

      [TaskManagementTaskHistoryActionType.DescriptionUpdated]: 'description.updated',
      [NotificationsPushActionsEnum.TaskDescriptionUpdated]: 'description.updated',

      [TaskManagementTaskHistoryActionType.AssigneeChanged]: 'taskManagement.history.actions.assigneeChanged',
      [NotificationsPushActionsEnum.TaskAssigneeChanged]: 'taskManagement.history.actions.assigneeChanged',

      [TaskManagementTaskHistoryActionType.MilestoneChanged]: 'taskManagement.history.actions.milestoneChanged',
      [NotificationsPushActionsEnum.TaskMilestoneChanged]: 'taskManagement.history.actions.milestoneChanged',

      [TaskManagementTaskHistoryActionType.DateStartUpdated]: 'taskManagement.history.actions.dateStartUpdated',
      [NotificationsPushActionsEnum.TaskDateStartUpdated]: 'taskManagement.history.actions.dateStartUpdated',

      [TaskManagementTaskHistoryActionType.DateDueUpdated]: 'taskManagement.history.actions.dateDueUpdated',
      [NotificationsPushActionsEnum.TaskDateDueUpdated]: 'taskManagement.history.actions.dateDueUpdated',

      [TaskManagementTaskHistoryActionType.TagsAdded]: 'taskManagement.history.actions.tagsAdded',
      [NotificationsPushActionsEnum.TaskTagsAdded]: 'taskManagement.history.actions.tagsAdded',

      [TaskManagementTaskHistoryActionType.TagsRemoved]: 'taskManagement.history.actions.tagsRemoved',
      [NotificationsPushActionsEnum.TaskTagsRemoved]: 'taskManagement.history.actions.tagsRemoved',

      [TaskManagementTaskHistoryActionType.FilesAdded]: 'taskManagement.history.actions.filesAdded',
      [NotificationsPushActionsEnum.TaskFilesAdded]: 'taskManagement.history.actions.filesAdded',

      [TaskManagementTaskHistoryActionType.FilesRemoved]: 'taskManagement.history.actions.filesRemoved',
      [NotificationsPushActionsEnum.TaskFilesRemoved]: 'taskManagement.history.actions.filesRemoved',

      [TaskManagementTaskHistoryActionType.WikiesAdded]: 'taskManagement.history.actions.wikiesAdded',
      [NotificationsPushActionsEnum.TaskWikisAdded]: 'taskManagement.history.actions.wikiesAdded',

      [TaskManagementTaskHistoryActionType.WikiesRemoved]: 'taskManagement.history.actions.wikiesRemoved',
      [NotificationsPushActionsEnum.TaskWikisRemoved]: 'taskManagement.history.actions.wikiesRemoved',

      [TaskManagementTaskHistoryActionType.LinksAdded]: 'taskManagement.history.actions.linksAdded',
      [NotificationsPushActionsEnum.TaskLinksAdded]: 'taskManagement.history.actions.linksAdded',

      [TaskManagementTaskHistoryActionType.LinksRemoved]: 'taskManagement.history.actions.linksRemoved',
      [NotificationsPushActionsEnum.TaskLinksRemoved]: 'taskManagement.history.actions.linksRemoved',

      [TaskManagementTaskHistoryActionType.ParticipantsAdded]: 'taskManagement.history.actions.participantsAdded',
      [NotificationsPushActionsEnum.TaskParticipantsAdded]: 'taskManagement.history.actions.participantsAdded',

      [TaskManagementTaskHistoryActionType.ParticipantsRemoved]: 'taskManagement.history.actions.participantsRemoved',
      [NotificationsPushActionsEnum.TaskParticipantsRemoved]: 'taskManagement.history.actions.participantsRemoved',

      [TaskManagementTaskHistoryActionType.CommentCreated]: 'taskManagement.history.actions.commentCreated',
      [NotificationsPushActionsEnum.TaskCommentCreated]: 'taskManagement.history.actions.commentCreated',

      [TaskManagementTaskHistoryActionType.CommentUpdated]: 'taskManagement.history.actions.commentUpdated',
      [NotificationsPushActionsEnum.TaskCommentUpdated]: 'taskManagement.history.actions.commentUpdated',

      [TaskManagementTaskHistoryActionType.CommentDeleted]: 'taskManagement.history.actions.commentDeleted',
      [NotificationsPushActionsEnum.TaskCommentDeleted]: 'taskManagement.history.actions.commentDeleted',

      [TaskManagementTaskHistoryActionType.ReporterChanged]: 'taskManagement.history.actions.reporterChanged',
      [NotificationsPushActionsEnum.TaskReporterChanged]: 'taskManagement.history.actions.reporterChanged',

      [TaskManagementTaskHistoryActionType.ChecklistCreated]: 'taskManagement.history.actions.checklistCreated',
      [NotificationsPushActionsEnum.TaskChecklistCreated]: 'taskManagement.history.actions.checklistCreated',

      [TaskManagementTaskHistoryActionType.ChecklistDeleted]: 'taskManagement.history.actions.checklistDeleted',
      [NotificationsPushActionsEnum.TaskChecklistDeleted]: 'taskManagement.history.actions.checklistDeleted',

      [TaskManagementTaskHistoryActionType.CheckboxDeleted]: 'taskManagement.history.actions.checkboxDeleted',
      [NotificationsPushActionsEnum.TaskCheckboxDeleted]: 'taskManagement.history.actions.checkboxDeleted',

      [TaskManagementTaskHistoryActionType.ChecklistUpdated]: 'taskManagement.history.actions.checklistUpdated',
      [NotificationsPushActionsEnum.TaskChecklistUpdated]: 'taskManagement.history.actions.checklistUpdated',

      [TaskManagementTaskHistoryActionType.CheckboxCreated]: 'taskManagement.history.actions.checkboxCreated',
      [NotificationsPushActionsEnum.TaskCheckboxCreated]: 'taskManagement.history.actions.checkboxCreated',

      [TaskManagementTaskHistoryActionType.CheckboxUpdated]: 'taskManagement.history.actions.checkboxUpdated',
      [NotificationsPushActionsEnum.TaskCheckboxUpdated]: 'taskManagement.history.actions.checkboxUpdated',

      [TaskManagementTaskHistoryActionType.CheckboxMoved]: 'taskManagement.history.actions.checkboxMoved',
      [NotificationsPushActionsEnum.TaskCheckboxMoved]: 'taskManagement.history.actions.checkboxMoved',
    };

    const handler = actionMapping[action];
    if (!handler) {
      console.warn(`[WARN] No handler for action: ${action}`);
      return '';
    }

    return t(handler);
  };

  const getSelectedStatusFilter = (): TaskManagementTasksStatusFilterEnum | TaskManagementMilestonesViewModeEnum => {
    const projectsStore = useProjectsStore();
    const activeType = projectsStore.statusFilter.activeType;

    switch (activeType) {
      case TaskManagementActiveSortTypeEnum.Tasks:
        return projectsStore.statusFilter.tasks;

      case TaskManagementActiveSortTypeEnum.Milestones:
        return projectsStore.statusFilter.milestones;

      default:
        return projectsStore.statusFilter.tasks;
    }
  };

  const getSelectedStatusFilterTitle = (
    status: TaskManagementTasksStatusFilterEnum | TaskManagementMilestonesViewModeEnum
  ): string => {
    const { t } = useI18n();
    switch (status) {
      case TaskManagementTasksStatusFilterEnum.All:
        return t('taskManagement.status.showAll');

      case TaskManagementTasksStatusFilterEnum.Open:
      case TaskManagementMilestonesViewModeEnum.Opened:
        return t('taskManagement.status.showOnlyOpened');

      case TaskManagementTasksStatusFilterEnum.Closed:
      case TaskManagementMilestonesViewModeEnum.Closed:
        return t('taskManagement.status.showOnlyClosed');
    }
  };

  const getTasksStatusFilters = (): TaskManagementModalMenuItemModel[] => {
    const data: TaskManagementModalMenuItemModel[] = [
      {
        title: getSelectedStatusFilterTitle(TaskManagementTasksStatusFilterEnum.All),
        icon: AppIconsEnum.None,
        disabled: false,
        value: TaskManagementTasksStatusFilterEnum.All,
      },
      {
        title: getSelectedStatusFilterTitle(TaskManagementTasksStatusFilterEnum.Open),
        icon: AppIconsEnum.None,
        disabled: false,
        value: TaskManagementTasksStatusFilterEnum.Open,
      },
      {
        title: getSelectedStatusFilterTitle(TaskManagementTasksStatusFilterEnum.Closed),
        icon: AppIconsEnum.None,
        disabled: false,
        value: TaskManagementTasksStatusFilterEnum.Closed,
      },
    ];
    return data;
  };

  return {
    getTaskMainMenu,
    getTaskMenuFeatures,
    getTaskMenuActions,
    getTaskModalMenuItems,
    taskManagementMilestonesHelper,
    menuSelectionHandling,
    taskManagementProjectSwitchHelper,
    getMainHeaderMenu,
    mainHeaderMenuSelectionHandling,
    getMainHeaderSearchModes,
    getTasksSortingModes,
    getMilestonesSortingModes,
    getProjectsSortingModes,
    taskManagementMainHeaderSearchHelper,
    accessToCreateProject,
    accessToDeleteTask,
    getSortTitle,
    getSelectedSort,
    getSelectedSortDirection,
    getAccessToTaskManagement,
    loadProject,
    loadTasks,
    refresh,
    setSortDirection,
    getHistoryTitle,
    getAccessToCreateTask,
    accessToCreateMilestone,
    getSelectedStatusFilter,
    getSelectedStatusFilterTitle,
    getTasksStatusFilters,
    getViewModeIcon,
  };
};
